import type { GpuServer } from '../../../types/models/GpuServer';
import { compareMoney, sumMoney } from '../../../utils/money';
import { isBilled, isTerminal } from './gpuServerStatus';

// Client-side billing estimates for the tenant GPU pages. They are APPROXIMATIONS (a later hourly
// debit can differ from `first_hour_ttc` by a cent or two), so the UI always prefixes them with "≈"
// and never blocks on them: the backend decides, and its refusal names the exact amount.

/** Servers whose next hour will be debited: `running`, `unpaid` and `starting`. */
export function billedServers(servers: GpuServer[]): GpuServer[] {
  return servers.filter((server) => isBilled(server.status));
}

/** ≈ the next hour of every billed server (Σ `first_hour_ttc`, exact cents); `null` if unknowable. */
export function nextHourTotal(servers: GpuServer[]): string | null {
  return sumMoney(billedServers(servers).map((server) => server.first_hour_ttc));
}

/**
 * ≈ what a start needs: the new server's first hour + the next hour of every billed server (the
 * backend's start reserve). `null` if unknowable.
 */
export function startReserve(productFirstHourTtc: string, servers: GpuServer[]): string | null {
  return sumMoney([productFirstHourTtc, ...billedServers(servers).map((server) => server.first_hour_ttc)]);
}

/** The earliest `paid_through` among billed servers (when the next debit — or deletion — is due). */
export function earliestPaidThrough(servers: GpuServer[]): string | null {
  let earliest: string | null = null;
  let earliestTs = Infinity;
  for (const server of billedServers(servers)) {
    const ts = Date.parse(server.paid_through);
    if (!Number.isNaN(ts) && ts < earliestTs) {
      earliestTs = ts;
      earliest = server.paid_through;
    }
  }
  return earliest;
}

/** Servers counting against the per-organisation cap: every non-terminal one. */
export function activeServerCount(servers: GpuServer[]): number {
  return servers.filter((server) => !isTerminal(server.status)).length;
}

/** `true` when `balance < needed`, `false` when it covers it, `null` when either is malformed. */
export function isShortOf(balance: string, needed: string): boolean | null {
  const cmp = compareMoney(balance, needed);
  return cmp === null ? null : cmp < 0;
}

/**
 * A VAT rate string (`"21.00"`) as a fr-BE percentage (`"21 %"`); the raw string if it is not a
 * number. Shared by the start wizard's review step and the budget totals.
 */
export function vatLabel(rate: string): string {
  const n = parseFloat(rate);
  return Number.isNaN(n) ? rate : `${n.toLocaleString('fr-BE')} %`;
}
